import { Directive, ElementRef, EventEmitter, HostListener, Input, NgZone, OnDestroy, Output } from '@angular/core';

/**
 * Drag a handle horizontally to resize a sibling panel that lives inside the
 * same flex container. The directive measures against the parent of the host
 * element, so the host should be the visual divider sitting between the
 * resizable panel and the rest of the layout.
 *
 * Emits the new pixel width of the resizable panel through `tdResizableDividerChange`.
 * The host component is responsible for binding that width back into the panel
 * (e.g. `[style.width.px]="leftWidth"`) and for any persistence (e.g. localStorage).
 *
 * Double-click on the handle resets to `tdResizableDividerDefault`.
 */
@Directive({
  selector: '[tdResizableDivider]',
  standalone: false,
})
export class ResizableDividerDirective implements OnDestroy {
  @Input() public tdResizableDividerMin = 280;
  @Input() public tdResizableDividerMaxRatio = 0.7;
  @Input() public tdResizableDividerDefault: number | null = null;
  @Input() public tdResizableDividerDisabled = false;

  @Output() public tdResizableDividerChange = new EventEmitter<number>();

  private activePointerId: number | null = null;
  private rafId: number | null = null;
  private pendingWidth: number | null = null;
  private previousBodyUserSelect: string | null = null;

  constructor(private host: ElementRef<HTMLElement>, private zone: NgZone) {}

  public ngOnDestroy(): void {
    this.releasePointer();
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  @HostListener('pointerdown', ['$event'])
  public onPointerDown(event: PointerEvent): void {
    if (this.tdResizableDividerDisabled || event.button !== 0) {
      return;
    }
    event.preventDefault();
    const handle = this.host.nativeElement;
    handle.setPointerCapture(event.pointerId);
    this.activePointerId = event.pointerId;

    this.previousBodyUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
  }

  @HostListener('pointermove', ['$event'])
  public onPointerMove(event: PointerEvent): void {
    if (this.activePointerId !== event.pointerId) {
      return;
    }
    const container = this.host.nativeElement.parentElement;
    if (!container) {
      return;
    }
    const rect = container.getBoundingClientRect();
    const max = Math.max(this.tdResizableDividerMin, rect.width * this.tdResizableDividerMaxRatio);
    const next = Math.min(Math.max(event.clientX - rect.left, this.tdResizableDividerMin), max);
    this.scheduleEmit(next);
  }

  @HostListener('pointerup', ['$event'])
  public onPointerUp(event: PointerEvent): void {
    if (this.activePointerId !== event.pointerId) {
      return;
    }
    this.releasePointer();
  }

  @HostListener('pointercancel', ['$event'])
  public onPointerCancel(event: PointerEvent): void {
    if (this.activePointerId !== event.pointerId) {
      return;
    }
    this.releasePointer();
  }

  @HostListener('dblclick')
  public onDoubleClick(): void {
    if (this.tdResizableDividerDisabled || this.tdResizableDividerDefault == null) {
      return;
    }
    this.tdResizableDividerChange.emit(this.tdResizableDividerDefault);
  }

  private scheduleEmit(width: number): void {
    this.pendingWidth = width;
    if (this.rafId !== null) {
      return;
    }
    this.zone.runOutsideAngular(() => {
      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        const value = this.pendingWidth;
        this.pendingWidth = null;
        if (value == null) {
          return;
        }
        this.zone.run(() => this.tdResizableDividerChange.emit(value));
      });
    });
  }

  private releasePointer(): void {
    const handle = this.host.nativeElement;
    if (this.activePointerId !== null && handle.hasPointerCapture(this.activePointerId)) {
      handle.releasePointerCapture(this.activePointerId);
    }
    this.activePointerId = null;
    if (this.previousBodyUserSelect !== null) {
      document.body.style.userSelect = this.previousBodyUserSelect;
      this.previousBodyUserSelect = null;
    }
  }
}
